<script setup>
import { ref, watch, computed } from "vue";
import { useModalStore } from '@/store/modal.store';

const props = defineProps({
  closeSignal: Boolean,
  required: false
});

watch(() => props.closeSignal, () => {
  if (props.closeSignal) {
    close();
  }
});

const modalStore = useModalStore();

const loading = ref(false);

const close = () => {
  if (!loading.value) {
    modalStore.closeModal();
  }
};

const typeMap = {
  'c': { icon: 'stylus_note', color: 'purple' },
  's': { icon: 'feedback', color: 'blue' },
  'o': { icon: 'open_in_new', color: 'purple' },
  'sy': { icon: 'check', color: 'light-blue' },
  'sn': { icon: 'close', color: 'yellow' },
  'sr': { icon: 'done_all', color: 'gray' },
  'sd': { icon: 'close', color: 'yellow' },
  'sw': { icon: 'water_damage', color: 'light-blue' },
  'ss': { icon: 'house_with_shield', color: 'light-blue' },
  'se': { icon: 'close', color: 'yellow' },
  'n': { icon: 'thumb_down', color: 'red' },
  'p': { icon: 'thumb_up', color: 'green' }
};

const getStatusAlertText = (type) => {
  const statusTextMap = {
    'p': 'Positive',
    'n': 'Negative'
  };
  return statusTextMap[type] || 'Current';
};

const getStatusClass = (type) => {
  const statusClassMap = {
    'p': 'status-positive',
    'n': 'status-negative'
  };
  return statusClassMap[type] || 'status-current';
};

const getStatusTypeClass = (type) => {
  const item = typeMap[type] || {};
  return item.color ? `basic__chat_status--${item.color}` : '';
};

const getCircleClass = (type) => {
  const circleClassMap = {
    'p': 'circle-positive',
    'n': 'circle-negative'
  };
  return circleClassMap[type] || 'circle-current';
};

const getIcon = (type) => {
  const item = typeMap[type] || {};
  return item.icon || 'feedback';
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return hours + ':' + minutes + ' ' + ampm;
};

const modalHeaderType = computed(() => {
  if (Array.isArray(modalStore.modalPayload) && modalStore.modalPayload.length > 0) {
    return modalStore.modalPayload[0].type;
  }
  return '';
});

const groupedTimeline = computed(() => {
  const groups = {};
  if (Array.isArray(modalStore.modalPayload)) {
    modalStore.modalPayload.forEach((item) => {
      const date = new Date(item?.created).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
    });
  }
  return Object.keys(groups).map((date) => ({
    date,
    items: groups[date]
  }));
});

const expandedItems = ref(new Set());

const toggleExpand = (itemId) => {
  if (expandedItems.value.has(itemId)) {
    expandedItems.value.delete(itemId);
  } else {
    expandedItems.value.add(itemId);
  }
};

const getItemText = (item) => {
  // If item has text, use it (for notes with actual text content)
  if (item.text && item.text.trim() !== '') {
    // For type 'p' or 'n' with text, show the text (not just "Responded")
    if (item.type === 'p' || item.type === 'n' || item.type === 'o') {
      return item.text;
    }
    return item.text;
  }
  
  // Fallback to default text if no text content
  if (item.type === 'p') {
    return 'Responded';
  } else if (item.type === 'n') {
    return 'User responded "No"';
  } else if (item.type === 'o') {
    return 'Opened';
  } else {
    return item.text || '';
  }
};

const shouldShowExpand = (item) => {
  const text = getItemText(item);
  return text && text.length > 100;
};

const getDisplayText = (item) => {
  const text = getItemText(item);
  if (!text) return '';
  
  const isExpanded = expandedItems.value.has(item.id);
  if (isExpanded || text.length <= 100) {
    return text;
  }
  return text.substring(0, 100) + '...';
};

</script>

<template>
  <div class="modal modal--active">
    <div class="modal__body">
      <div class="modal__header statusAlert">
        <div class="status-wrap">
          <span :class="['status-circle', getCircleClass(modalHeaderType)]"></span>
          <span :class="[getStatusClass(modalHeaderType)]">
          {{ getStatusAlertText(modalHeaderType) }}
        </span>
        </div>
        <button @click="close" class="modal__close">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
      <div class="modal__main">
        <div class="main-block">
          <div class="main-block">
            <div class="basic__chat_wrap" v-if="Array.isArray(modalStore.modalPayload) && modalStore.modalPayload.length > 0">
              <template v-for="(dateGroup, index) in groupedTimeline" :key="index">
                <div class="basic__chat_date">
                  <p class="basic__chat_date-text">{{ formatDate(dateGroup.date) }}</p>
                </div>
                <div v-for="item in dateGroup.items" :key="item.id" class="basic__chat_alert">
                  <div :class="['basic__chat_status', getStatusTypeClass(item.type)]">
                    <span class="material-symbols-outlined">{{ getIcon(item.type) }}</span>
                  </div>
                  <div class="basic__chat_item">
                    <p class="basic__chat_text">
                      {{ getDisplayText(item) }}
                      <button 
                        v-if="shouldShowExpand(item)" 
                        @click="toggleExpand(item.id)"
                        class="expand-text-btn"
                      >
                        {{ expandedItems.has(item.id) ? 'Show less' : 'Show more' }}
                      </button>
                    </p>
                  </div>
                  <p class="basic__chat_time">{{ formatTime(item.created) }}</p>
                </div>
              </template>
            </div>
            <div v-else>
              <p>No data available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal__main {
  justify-content: flex-start;
  padding: 35px 0 0 0;
}
.modal__body {
  max-width: 848px;
  min-height: 760px;
}
.modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.modal__close {
  background: none;
  border: none;
  cursor: pointer;
}
.main-block {
  display: grid;
  justify-items: center;
  justify-content: center;
  align-items: center;
}
.basic__chat_wrap {
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 0 25px 25px;
  /* height: calc(100vh - 620px); */
  /* height: calc(100vh - 530px); */
  height: calc(100vmin - 240px)!important;
  /* height: unset!important; */
  overflow: auto;
}
.basic__chat_alert {
 position: relative;
 display: grid;
 grid-template-columns: 40px auto auto;
 align-items: start;
 gap: 25px;
}

.basic__chat_alert::before {
 content: '';
 position: absolute;
 left: 20px;
 bottom: -25px;
 height: 300%;
 width: 1px;
 background-color: #dbeafe;
 z-index: -1;
}

.basic__chat_text {
 text-align: left;
 width: 100%;
 max-width: 600px;
 color: #334155;
 font-family: 'Inter', sans-serif;
 font-size: 16px;
 font-style: normal;
 font-weight: 400;
 line-height: 160%;
}

.basic__chat_date:first-of-type {
  margin-top: 0;
}

.basic__chat_time {
 color: #64748b;
 margin-left: 0!important;
}

.basic__chat_status {
 margin-top: 20px;
 display: flex;
 align-items: center;
 justify-content: center;
 width: 40px;
 height: 40px;
 border-radius: 50%;
 border: 1px solid #2563EB;
 color: #2563eb;
 background-color: #fff;
}

.basic__chat_status--purple {
  border: 1px solid purple;
  color: purple;
}
.basic__chat_status--blue {
  border: 1px solid blue;
  color: blue;
}
.basic__chat_status--light-blue {
  border: 1px solid lightblue;
  color: lightblue;
}
.basic__chat_status--yellow {
  border: 1px solid yellow;
  color: yellow;
}
.basic__chat_status--gray {
  border: 1px solid gray;
  color: gray;
}
.basic__chat_status--red {
  border: 1px solid red;
  color: red;
}
.basic__chat_status--green {
  border: 1px solid green;
  color: green;
}

.basic__chat_date-text {
  text-align: left;
  color: #64748B;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
  letter-spacing: 0.2px;
}

.status-wrap {
 display: flex;
 align-items: center;
}

.statusAlert {
 display: flex;
 align-items: center;
}

.status-circle {
 display: block;
 width: 6px;
 height: 6px;
 border-radius: 50%;
 margin-right: 8px;
}

.status-positive {
 color: #34c759; /* Green color */
}

.circle-positive {
  background-color: #34c759; /* Green color */
}

.status-negative {
  color: #ff3b30; /* Red color */
}

.circle-negative {
  background-color: #ff3b30; /* Red color */
}

.status-current {
  color: #ffcc00; /* Yellow color */
}

.circle-current {
  background-color: #ffcc00; /* Yellow color */
}

.expand-text-btn {
  background: none;
  border: none;
  color: #2563EB;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  text-decoration: underline;
  font-size: 14px;
  font-family: inherit;
}

.expand-text-btn:hover {
  color: #1e40af;
}
</style>
